/**
 * ProposalBuilderPage — Admin · Proposal Builder (Wave 3, revised 2026-06-30)
 *
 * Left panel: flowchart showing the proposal assembly order for the selected
 * category (shared → conditional-calc → per-category → variant).
 * Right panel: WYSIWYG section editor — EVERY node is editable (Cover, Our Story,
 * Installations, Tech Spec, Quotation, Sizing, ROI, Engineering Notes, …).
 *
 * Matches the Claude Design `stellar-proposal-builder.jsx` + `stellar-proposal-editor.jsx`.
 * Tokens: AURA canonical CSS vars defined in src/index.css — --ivory-0/1/2/3,
 * --line, --line-strong, --ink-0/1/2/3/4, --saffron, --forest. (The earlier
 * --card / --surface-2 / --divider tokens are UNDEFINED in AURA's CSS, which is
 * why the active category tab rendered dark-text-on-dark — fixed here.)
 * No `primary` Tailwind classes anywhere.
 *
 * Endpoints consumed:
 *   GET    /api/v1/admin/proposal-builder/categories
 *   GET    /api/v1/admin/proposal-builder/sections?category=<key>
 *   PATCH  /api/v1/admin/proposal-builder/sections/:id            (save by id)
 *   POST   /api/v1/admin/proposal-builder/sections                (upsert by key+category)
 *   POST   /api/v1/admin/proposal-builder/sections/reorder
 *   POST   /api/v1/admin/proposal-builder/sections/:id/revert
 *   DELETE /api/v1/admin/proposal-builder/sections/:id
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ChevronUp,
  ChevronDown,
  Code,
  Eye,
  Maximize2,
  Minimize2,
  EyeOff,
  RotateCcw,
  Plus,
  Trash2,
  GitBranch,
  Calculator,
  Image,
  BookOpen,
  ExternalLink,
  Layers,
  AlignLeft,
  Bold,
  Italic,
  List,
  Link,
  X,
  Check,
  GripVertical,
} from 'lucide-react';
import {
  getCategories,
  getSections,
  patchSection,
  reorderSections,
  revertSection,
  createSection,
  deleteSection,
  saveSectionContent,
  previewProposal,
  previewSectionHtml,
  type ProposalCategory,
  type ProposalSection,
} from '../../api/proposalBuilder';

// ─── Types ───────────────────────────────────────────────────────────────────

interface SaveToast {
  visible: boolean;
  msg: string;
}

// ─── Tiny helpers ─────────────────────────────────────────────────────────────

function scopeLabel(scope: string): string {
  return (
    {
      shared: 'Shared',
      category: 'Category',
      conditional_calculator: 'Conditional',
    }[scope] ?? scope
  );
}

function scopeColor(scope: string): string {
  return (
    {
      shared: 'var(--forest)',
      category: 'var(--saffron)',
      conditional_calculator: '#6B5B8C',
    }[scope] ?? 'var(--ink-3)'
  );
}

function scopeBg(scope: string): string {
  return (
    {
      shared: 'rgba(60,120,80,.10)',
      category: 'rgba(201,138,59,.12)',
      conditional_calculator: '#EFE9F5',
    }[scope] ?? 'var(--ivory-2)'
  );
}

function sectionIcon(section: ProposalSection) {
  const k = section.key;
  if (k === 'cover_page' || k === 'cover') return <Image size={16} />;
  if (k === 'installation_gallery' || k === 'installations') return <Layers size={16} />;
  if (k === 'about_us') return <BookOpen size={16} />;
  if (k === 'sizing_recap' || k === 'roi_tco' || k === 'quotation_table') return <Calculator size={16} />;
  return <AlignLeft size={16} />;
}

// ─── Pill ─────────────────────────────────────────────────────────────────────

function ScopePill({ scope, dashed }: { scope: string; dashed?: boolean }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 18,
        padding: '0 7px',
        borderRadius: 999,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '.04em',
        textTransform: 'uppercase' as const,
        background: scopeBg(scope),
        color: scopeColor(scope),
        border: dashed ? `1px dashed ${scopeColor(scope)}` : 'none',
        whiteSpace: 'nowrap' as const,
      }}
    >
      {scopeLabel(scope)}
    </span>
  );
}

// ─── Toast ────────────────────────────────────────────────────────────────────

function SaveToastBanner({ toast }: { toast: SaveToast }) {
  if (!toast.visible) return null;
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        background: 'var(--ink-1)',
        color: '#fff',
        borderRadius: 10,
        padding: '10px 18px',
        fontSize: 13,
        fontWeight: 500,
        boxShadow: '0 4px 16px rgba(0,0,0,.24)',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        pointerEvents: 'none',
      }}
    >
      <Check size={14} />
      {toast.msg}
    </div>
  );
}

// ─── Flowchart ────────────────────────────────────────────────────────────────

function Connector({ dashed, label }: { dashed?: boolean; label?: string }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        height: 30,
      }}
    >
      <div
        style={{
          width: 0,
          flex: 1,
          borderLeft: `2px ${dashed ? 'dashed' : 'solid'} var(--line)`,
        }}
      />
      {label && (
        <span
          style={{
            position: 'absolute',
            left: 'calc(50% + 8px)',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: 10,
            fontWeight: 600,
            color: 'var(--ink-3)',
            background: 'var(--ivory-0)',
            padding: '0 4px',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </span>
      )}
      <svg width="11" height="8" viewBox="0 0 11 8" style={{ marginTop: -1 }}>
        <path d="M5.5 8L0 0h11z" fill="var(--line)" />
      </svg>
    </div>
  );
}

function FlowNode({
  section,
  selected,
  onSelect,
  onToggle,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  dragHandleProps,
}: {
  section: ProposalSection;
  selected: boolean;
  onSelect: () => void;
  onToggle?: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  dragHandleProps?: React.HTMLAttributes<HTMLSpanElement>;
}) {
  const dashed = section.scope === 'conditional_calculator';
  const enabled = section.isActive !== false;
  return (
    <div
      onClick={onSelect}
      style={{
        width: 300,
        background: selected ? 'var(--ivory-2)' : 'var(--ivory-0)',
        cursor: 'pointer',
        border: `1.5px ${dashed ? 'dashed' : 'solid'} ${
          selected ? 'var(--saffron)' : 'var(--line)'
        }`,
        borderRadius: 11,
        padding: '11px 12px',
        boxShadow: selected
          ? '0 0 0 3px rgba(201,138,59,.14), 0 1px 3px rgba(0,0,0,.06)'
          : '0 1px 2px rgba(0,0,0,.05)',
        transition: 'box-shadow .14s, border-color .14s',
        opacity: enabled ? 1 : 0.5,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        {dragHandleProps && (
          <span
            {...dragHandleProps}
            style={{
              color: 'var(--ink-3)',
              cursor: 'grab',
              display: 'flex',
              marginLeft: -4,
              flexShrink: 0,
            }}
            title="Drag to reorder"
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical size={14} />
          </span>
        )}
        <span
          style={{
            width: 30,
            height: 30,
            borderRadius: 8,
            background: 'var(--ivory-2)',
            border: '1px solid var(--line)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            color: 'var(--ink-2)',
          }}
        >
          {sectionIcon(section)}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13.5,
              fontWeight: 600,
              color: 'var(--ink-1)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {section.displayName}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 4 }}>
            <ScopePill scope={section.scope} dashed={dashed} />
            {section.engineDerived && (
              <span style={{ fontSize: 10, color: 'var(--ink-3)' }}>engine</span>
            )}
          </div>
        </div>
        {/* Up/Down reorder buttons for category-scoped editable sections */}
        {(onMoveUp || onMoveDown) && (
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              disabled={!canMoveUp}
              onClick={onMoveUp}
              title="Move up"
              style={{
                width: 20,
                height: 20,
                border: '1px solid var(--line)',
                borderRadius: 4,
                background: 'var(--ivory-0)',
                cursor: canMoveUp ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: canMoveUp ? 1 : 0.35,
                padding: 0,
              }}
            >
              <ChevronUp size={12} />
            </button>
            <button
              disabled={!canMoveDown}
              onClick={onMoveDown}
              title="Move down"
              style={{
                width: 20,
                height: 20,
                border: '1px solid var(--line)',
                borderRadius: 4,
                background: 'var(--ivory-0)',
                cursor: canMoveDown ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: canMoveDown ? 1 : 0.35,
                padding: 0,
              }}
            >
              <ChevronDown size={12} />
            </button>
          </div>
        )}
        {/* Visibility toggle for editable sections */}
        {onToggle && section.editable && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            title={enabled ? 'Hide section' : 'Show section'}
            style={{
              width: 26,
              height: 26,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: enabled ? 'var(--ink-2)' : 'var(--ink-3)',
              flexShrink: 0,
              borderRadius: 6,
              padding: 0,
            }}
          >
            {enabled ? <Eye size={14} /> : <EyeOff size={14} />}
          </button>
        )}
      </div>
    </div>
  );
}

function DiamondNode({
  selected,
  onSelect,
}: {
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <div
      onClick={onSelect}
      style={{
        position: 'relative',
        width: 300,
        display: 'flex',
        justifyContent: 'center',
        cursor: 'pointer',
      }}
    >
      <div
        style={{
          width: 150,
          height: 76,
          background: selected ? 'var(--ivory-2)' : 'var(--ivory-0)',
          border: `1.5px solid ${selected ? 'var(--saffron)' : 'var(--line)'}`,
          transform: 'rotate(45deg)',
          borderRadius: 12,
          boxShadow: selected
            ? '0 0 0 3px rgba(201,138,59,.14)'
            : '0 1px 2px rgba(0,0,0,.05)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          padding: '0 28px',
          textAlign: 'center',
        }}
      >
        <GitBranch size={15} style={{ color: 'var(--ink-2)', marginBottom: 3 }} />
        <div
          style={{
            fontSize: 10.5,
            fontWeight: 600,
            color: 'var(--ink-1)',
            lineHeight: 1.25,
          }}
        >
          has calculator AND
          <br />
          source = calculator?
        </div>
      </div>
    </div>
  );
}

function ProposalFlowchart({
  category,
  sections,
  selectedId,
  onSelect,
  onToggle,
  onMoveUp,
  onMoveDown,
}: {
  category: string;
  sections: ProposalSection[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onToggle: (id: string) => void;
  onMoveUp: (id: string) => void;
  onMoveDown: (id: string) => void;
}) {
  // Render in the REAL proposal order (sortOrder) as ONE top→bottom flow, with
  // scope shown as a pill per node — mirroring how the proposal actually
  // assembles, instead of grouping shared/category/conditional into lanes
  // (which made Installation Gallery appear last when it's really 3rd).
  const ordered = [...sections].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <div
      style={{
        padding: '28px 24px 40px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minHeight: '100%',
      }}
    >
      {/* Section-order header */}
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--ink-3)',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          marginBottom: 16,
        }}
      >
        Proposal order · top → bottom
      </div>

      {/* Sections in the REAL render order; scope is a pill per node.
          Conditional (calculator) sections show a dashed "Conditional" pill and
          only appear for categories that have a calculator. */}
      {ordered.map((s, i) => {
        const prevEditable = ordered.slice(0, i).some((x) => x.editable && !!x.id);
        const nextEditable = ordered.slice(i + 1).some((x) => x.editable && !!x.id);
        return (
          <div key={s.id ?? s.key}>
            <FlowNode
              section={s}
              selected={selectedId === (s.id ?? s.key)}
              onSelect={() => onSelect(s.id ?? s.key)}
              onToggle={s.editable && s.id ? () => onToggle(s.id!) : undefined}
              onMoveUp={s.editable && s.id ? () => onMoveUp(s.id!) : undefined}
              onMoveDown={s.editable && s.id ? () => onMoveDown(s.id!) : undefined}
              canMoveUp={s.editable && !!s.id && prevEditable}
              canMoveDown={s.editable && !!s.id && nextEditable}
            />
            <Connector />
          </div>
        );
      })}
    </div>
  );
}

// ─── WYSIWYG editor ───────────────────────────────────────────────────────────

// ─── Dynamic-field tokens ↔ visible chips ─────────────────────────────────────
// Dynamic fields are stored as {{token}} so they fill per-customer at render.
// In the editor we show them as labelled, non-editable chips ([Customer Name],
// [Pricing Table], …) so the admin edits the static text around them without
// freezing the live values. htmlToEditor() turns tokens → chips for display;
// editorToHtml() turns chips → tokens for saving.

const TOKEN_LABELS: Record<string, string> = {
  'customer.name': 'Customer Name',
  'customer.phone': 'Customer Phone',
  'customer.email': 'Customer Email',
  'customer.address': 'Customer Address',
  'company.name': 'Company Name',
  'proposal.number': 'Proposal No.',
  'proposal.validUntil': 'Valid Until',
  date: 'Date',
  'totals.total': 'Grand Total',
  'totals.subtotal': 'Subtotal',
  'totals.tax': 'GST',
  'solution.headline': 'Solution',
  'solution.subtitle': 'Solution Subtitle',
  'salesRep.name': 'Sales Rep',
  quotationRows: 'Pricing Table',
  galleryItems: 'Installation Gallery',
  specRows: 'Spec Table',
  sizingTable: 'Sizing Table',
  roiGrid: 'ROI Grid',
  engineeringNotesList: 'Engineering Notes',
};

function tokenLabel(t: string): string {
  return TOKEN_LABELS[t] ?? t.replace(/[._]/g, ' ');
}

// Whole-table/grid fragment tokens render as a wide block chip; scalars inline.
const FRAGMENT_TOKENS = new Set([
  'quotationRows', 'galleryItems', 'specRows', 'sizingTable', 'roiGrid', 'engineeringNotesList',
]);

const TOKEN_CHIP_CSS = `
  .aura-token-chip{display:inline-block;padding:1px 9px;margin:0 1px;border-radius:6px;
    background:#EEF2FF;color:#3730A3;border:1px solid #C7D2FE;font-size:.82em;font-weight:700;
    line-height:1.5;white-space:nowrap;vertical-align:baseline;cursor:default;user-select:all;
    font-family:Manrope,system-ui,sans-serif;}
  .aura-token-chip[data-fragment="1"]{display:block;text-align:center;padding:10px;margin:6px 0;
    background:#F5F3FF;color:#5B21B6;border:1px dashed #C4B5FD;font-size:.78em;border-radius:9px;}
  .aura-token-chip::before{content:"";}`;

/** {{token}} → labelled non-editable chip span (for the editor display). */
function htmlToEditor(html: string): string {
  return (html || '').replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_m, p: string) => {
    const frag = FRAGMENT_TOKENS.has(p) ? ' data-fragment="1"' : '';
    const label = FRAGMENT_TOKENS.has(p) ? `📊 ${tokenLabel(p)}` : tokenLabel(p);
    return `<span class="aura-token-chip" data-token="${p}"${frag} contenteditable="false">${label}</span>`;
  });
}

/** chip span → {{token}} (for saving). Tolerant of browser attribute reordering. */
function editorToHtml(html: string): string {
  if (!html) return '';
  return html.replace(
    /<span\b[^>]*\bdata-token="([\w.]+)"[^>]*>[\s\S]*?<\/span>/gi,
    (_m, t: string) => `{{${t}}}`,
  );
}

function WysiwygEditor({
  html,
  css,
  tall,
  onChange,
}: {
  html: string;
  css?: string;
  tall?: boolean;
  onChange: (html: string) => void;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [color, setColor] = useState('#1B1713');

  // Build the isolated editor document. The iframe loads the proposal's OWN
  // stylesheet so the section renders EXACTLY like the proposal (fonts, brand
  // colours, grid layout), with the body contenteditable for in-place editing.
  // Rebuilt only when css changes; html is synced separately so live edits
  // aren't clobbered.
  useEffect(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(
      '<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank"><style>' +
        (css || '') +
        '\nhtml,body{background:#fff;} body{margin:0;padding:16px 18px;min-height:120px;box-sizing:border-box;} body:focus{outline:none;} img{max-width:100%;}' +
        TOKEN_CHIP_CSS +
        '</style></head><body contenteditable="true" spellcheck="false">' +
        htmlToEditor(html) +
        '</body></html>',
    );
    doc.close();
    const body = doc.body;
    // Read back as tokens (chips → {{token}}) so what's SAVED keeps the dynamics.
    const handler = () => onChange(editorToHtml(body.innerHTML));
    body.addEventListener('input', handler);
    return () => body.removeEventListener('input', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [css]);

  // Sync external html (HTML tab edits, or switching sections) into the iframe
  // without stomping an in-progress edit. Compare in token-space.
  useEffect(() => {
    const doc = iframeRef.current?.contentDocument;
    if (doc && doc.body && editorToHtml(doc.body.innerHTML) !== (html || '')) {
      doc.body.innerHTML = htmlToEditor(html);
    }
  }, [html]);

  const exec = (cmd: string, val?: string) => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    doc.body.focus();
    doc.execCommand(cmd, false, val);
    onChange(editorToHtml(doc.body.innerHTML));
  };

  const toolbarBtnStyle: React.CSSProperties = {
    width: 30,
    height: 28,
    border: 'none',
    background: 'transparent',
    borderRadius: 6,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--ink-2)',
    padding: 0,
  };

  const Sep = () => (
    <span
      style={{
        width: 1,
        height: 18,
        background: 'var(--line)',
        margin: '0 3px',
        flexShrink: 0,
      }}
    />
  );

  return (
    <div
      style={{
        border: '1px solid var(--line)',
        borderRadius: 9,
        overflow: 'hidden',
        background: 'var(--ivory-0)',
      }}
    >
      {/* toolbar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          padding: '5px 6px',
          borderBottom: '1px solid var(--line)',
          background: 'var(--ivory-2)',
          flexWrap: 'wrap',
        }}
      >
        <select
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => exec('formatBlock', e.target.value)}
          defaultValue="p"
          style={{
            height: 28,
            border: '1px solid var(--line)',
            borderRadius: 6,
            background: 'var(--ivory-0)',
            fontSize: 12,
            color: 'var(--ink-1)',
            padding: '0 6px',
            cursor: 'pointer',
            marginRight: 4,
          }}
        >
          <option value="p">Paragraph</option>
          <option value="h1">Heading 1</option>
          <option value="h2">Heading 2</option>
          <option value="h3">Heading 3</option>
          <option value="blockquote">Quote</option>
        </select>
        <button
          style={toolbarBtnStyle}
          onMouseDown={(e) => {
            e.preventDefault();
            exec('bold');
          }}
          title="Bold"
        >
          <Bold size={14} />
        </button>
        <button
          style={toolbarBtnStyle}
          onMouseDown={(e) => {
            e.preventDefault();
            exec('italic');
          }}
          title="Italic"
        >
          <Italic size={14} />
        </button>
        <Sep />
        <button
          style={toolbarBtnStyle}
          onMouseDown={(e) => {
            e.preventDefault();
            exec('insertUnorderedList');
          }}
          title="Bulleted list"
        >
          <List size={14} />
        </button>
        <Sep />
        <button
          style={toolbarBtnStyle}
          onMouseDown={(e) => {
            e.preventDefault();
            exec('createLink', prompt('URL:') ?? 'https://');
          }}
          title="Link"
        >
          <Link size={14} />
        </button>
        {/* Color picker */}
        <label
          title="Text colour"
          style={{
            width: 30,
            height: 28,
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            position: 'relative',
          }}
        >
          <span
            style={{
              width: 15,
              height: 15,
              borderRadius: 4,
              background: color,
              border: '1px solid var(--line)',
            }}
          />
          <input
            type="color"
            value={color}
            onChange={(e) => {
              setColor(e.target.value);
              exec('foreColor', e.target.value);
            }}
            style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
          />
        </label>
      </div>
      {/* styled editable area — proposal stylesheet in an isolated iframe so
          the section renders exactly like the proposal AND stays editable. */}
      <iframe
        ref={iframeRef}
        title="Section editor"
        style={{
          width: '100%',
          height: tall ? '78vh' : 440,
          border: 'none',
          background: '#fff',
          display: 'block',
        }}
      />
    </div>
  );
}

// ─── Field helpers ────────────────────────────────────────────────────────────

function FieldLabel({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ marginBottom: 6 }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.07em',
            color: 'var(--ink-3)',
          }}
        >
          {label}
        </span>
        {hint && (
          <span
            style={{ fontSize: 11.5, color: 'var(--ink-3)', marginLeft: 6 }}
          >
            — {hint}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

// ─── Section Editor panel ─────────────────────────────────────────────────────

function SectionEditorPanel({
  section,
  realHtml,
  proposalCss,
  fullscreen,
  onSaveContent,
  onRevert,
  onDelete,
  saving,
}: {
  section: ProposalSection | null;
  /** The section's REAL default-rendered HTML (from the proposal), loaded when
   *  there's no saved override yet so the editor shows the actual markup. */
  realHtml?: string;
  /** The proposal's own stylesheet, injected into the WYSIWYG iframe so the
   *  section renders exactly like the proposal. */
  proposalCss?: string;
  /** When the editor panel is in full-screen, give the WYSIWYG more height. */
  fullscreen?: boolean;
  /** Save content: PATCH by id when present, else upsert by key+category. */
  onSaveContent: (section: ProposalSection, contentHtml: string) => void;
  onRevert: (id: string) => void;
  onDelete: (id: string) => void;
  saving: boolean;
}) {
  const [localHtml, setLocalHtml] = useState('');
  const [dirty, setDirty] = useState(false);
  const [editMode, setEditMode] = useState<'visual' | 'html'>('visual');

  // Reset local state when the selected section changes. Key on `key` (unique
  // per category) because upsert-pending sections share id === null.
  // When the section has NO saved override (id === null), start from the REAL
  // rendered section HTML (realHtml) instead of the descriptor stub.
  useEffect(() => {
    const hasOverride = !!section?.id;
    const initial = hasOverride
      ? (section?.contentHtml ?? '')
      : (realHtml ?? section?.contentHtml ?? '');
    setLocalHtml(initial);
    setDirty(false);
  }, [section?.key, section?.id, realHtml]);

  if (!section) {
    return (
      <div
        style={{
          padding: 40,
          textAlign: 'center',
          color: 'var(--ink-3)',
          fontSize: 13,
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 12,
            background: 'var(--ivory-2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 14px',
            color: 'var(--ink-3)',
          }}
        >
          <Layers size={22} />
        </div>
        Select a node in the flowchart to edit its content and visibility.
      </div>
    );
  }

  const isVariant = (section as any).id === '__variant__';
  const isDiamond = (section as any).id === '__diamond__';

  if (isVariant || isDiamond) {
    return (
      <div style={{ padding: 20 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 16,
          }}
        >
          <span
            style={{
              width: 36,
              height: 36,
              borderRadius: 9,
              background: 'var(--ivory-2)',
              border: '1px solid var(--line)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--ink-2)',
            }}
          >
            <GitBranch size={18} />
          </span>
          <div>
            <div
              style={{
                fontSize: 17,
                fontWeight: 600,
                color: 'var(--ink-1)',
              }}
            >
              {isDiamond ? 'Calculator branch' : 'Per-product variant'}
            </div>
            <ScopePill scope="conditional_calculator" />
          </div>
        </div>
        <p style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.55 }}>
          {isDiamond
            ? 'This decision point is rendered entirely by the PDF engine. When the proposal was sourced from a calculator, the sizing recap and ROI sections are inserted automatically.'
            : 'Per-product variant rows (e.g. capacity, weight) are resolved per SKU at PDF render time and cannot be edited here.'}
        </p>
      </div>
    );
  }

  const handleHtmlChange = (html: string) => {
    setLocalHtml(html);
    setDirty(true);
  };

  const handleSave = () => {
    if (dirty) {
      // PATCH by id when present, else upsert by key+category — works for every
      // section including ones that only had engine-default content (id === null).
      onSaveContent(section, localHtml);
      setDirty(false);
    }
  };

  const isInstallationGallery =
    section.key === 'installation_gallery' || section.key === 'installations';
  const installationsHref = section.externalLink
    ? `${section.externalLink}${
        section.externalLinkCategory
          ? `?category=${encodeURIComponent(section.externalLinkCategory)}`
          : ''
      }`
    : '/admin/installations';

  return (
    <div style={{ padding: 16 }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 16,
        }}
      >
        <span
          style={{
            width: 36,
            height: 36,
            borderRadius: 9,
            background: 'var(--ivory-2)',
            border: '1px solid var(--line)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--ink-2)',
          }}
        >
          {sectionIcon(section)}
        </span>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 17,
              fontWeight: 600,
              color: 'var(--ink-1)',
              marginBottom: 4,
            }}
          >
            {section.displayName}
          </div>
          <ScopePill scope={section.scope} />
        </div>
      </div>

      {/* Every section is editable — WYSIWYG content editor. */}
      <>
          {/* Installation gallery: prominent "Manage" button to the installations
              admin (per-category), PLUS the editable intro-copy WYSIWYG below. */}
          {isInstallationGallery && (
            <FieldLabel
              label="Installation photos"
              hint="Photos are managed per-category in the Installations admin."
            >
              <a
                href={installationsHref}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 7,
                  height: 38,
                  padding: '0 16px',
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--ivory-0)',
                  textDecoration: 'none',
                  border: '1px solid var(--saffron-600)',
                  borderRadius: 8,
                  background: 'var(--saffron)',
                  boxShadow: '0 1px 2px rgba(27,23,19,.08)',
                }}
              >
                Manage in Installations
                <ExternalLink size={15} />
              </a>
            </FieldLabel>
          )}

          {/* Section content — Visual (WYSIWYG) ⇄ HTML source toggle. Both edit
              the same `localHtml`, so switching reflects edits both ways. */}
          <FieldLabel
            label={isInstallationGallery ? 'Intro copy' : 'Section content'}
          >
            <div
              style={{
                display: 'flex',
                gap: 4,
                marginBottom: 8,
                padding: 3,
                background: 'var(--ivory-2)',
                border: '1px solid var(--line)',
                borderRadius: 8,
                width: 'fit-content',
              }}
            >
              {(['visual', 'html'] as const).map((m) => (
                <button
                  key={m}
                  onClick={() => setEditMode(m)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    height: 28,
                    padding: '0 12px',
                    fontSize: 12,
                    fontWeight: 600,
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    color: editMode === m ? 'var(--ivory-0)' : 'var(--ink-2)',
                    background: editMode === m ? 'var(--ink-1)' : 'transparent',
                  }}
                >
                  {m === 'visual' ? <Eye size={13} /> : <Code size={13} />}
                  {m === 'visual' ? 'Visual' : 'HTML'}
                </button>
              ))}
            </div>
            {editMode === 'visual' ? (
              <WysiwygEditor html={localHtml} css={proposalCss} tall={fullscreen} onChange={handleHtmlChange} />
            ) : (
              <textarea
                value={localHtml}
                onChange={(e) => handleHtmlChange(e.target.value)}
                spellCheck={false}
                style={{
                  width: '100%',
                  minHeight: 320,
                  padding: 12,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: 12.5,
                  lineHeight: 1.6,
                  color: 'var(--ink-1)',
                  background: 'var(--ivory-0)',
                  border: '1px solid var(--line)',
                  borderRadius: 9,
                  resize: 'vertical',
                  whiteSpace: 'pre',
                  overflowWrap: 'normal',
                  tabSize: 2,
                }}
              />
            )}
          </FieldLabel>

          {/* Actions row */}
          <div
            style={{
              display: 'flex',
              gap: 8,
              flexWrap: 'wrap',
              marginTop: 4,
            }}
          >
            <button
              onClick={handleSave}
              disabled={!dirty || saving}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                height: 34,
                padding: '0 14px',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                border: 'none',
                background: dirty ? 'var(--ink-1)' : 'var(--ivory-2)',
                color: dirty ? 'var(--ivory-0)' : 'var(--ink-3)',
                cursor: dirty ? 'pointer' : 'not-allowed',
                transition: 'background .12s',
              }}
            >
              <Check size={13} />
              {saving ? 'Saving…' : 'Save'}
            </button>

            <button
              onClick={() => section.id && onRevert(section.id)}
              disabled={saving || !section.id}
              title="Revert to previous save (restore from snapshot)"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                height: 34,
                padding: '0 12px',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 500,
                border: '1px solid var(--line)',
                background: 'var(--ivory-0)',
                color: 'var(--ink-2)',
                cursor: 'pointer',
              }}
            >
              <RotateCcw size={13} />
              Revert to previous save
            </button>

            <button
              onClick={() => {
                if (
                  section.id &&
                  window.confirm(
                    `Delete "${section.displayName}"? This cannot be undone.`,
                  )
                ) {
                  onDelete(section.id);
                }
              }}
              disabled={saving || !section.id}
              title="Delete this section"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                height: 34,
                padding: '0 12px',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 500,
                border: '1px solid var(--line)',
                background: 'var(--ivory-0)',
                color: '#b91c1c',
                cursor: 'pointer',
                marginLeft: 'auto',
              }}
            >
              <Trash2 size={13} />
              Delete
            </button>
          </div>
      </>
    </div>
  );
}

// ─── Preview drawer ───────────────────────────────────────────────────────────

function PreviewDrawer({
  open,
  onClose,
  category,
  categoryLabel,
}: {
  open: boolean;
  onClose: () => void;
  category: string;
  categoryLabel: string;
}) {
  const [html, setHtml] = useState<string | null>(null);
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const fetchPreview = useCallback(async (cat: string) => {
    if (!cat) return;
    setFetching(true);
    setFetchError(null);
    setHtml(null);
    try {
      const res = await previewProposal(cat);
      setHtml(res.html);
    } catch (e: any) {
      setFetchError(e?.response?.data?.error ?? e?.message ?? 'Preview failed');
    } finally {
      setFetching(false);
    }
  }, []);

  // Auto-fetch when drawer opens (or category changes while open)
  useEffect(() => {
    if (open && category) fetchPreview(category);
  }, [open, category, fetchPreview]);

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 60,
        pointerEvents: open ? 'auto' : 'none',
      }}
    >
      {/* backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(27,23,19,.34)',
          backdropFilter: 'blur(2px)',
          opacity: open ? 1 : 0,
          transition: 'opacity .24s',
        }}
      />
      {/* panel */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: 560,
          background: 'var(--ivory-0)',
          borderLeft: '1px solid var(--line)',
          boxShadow: '-4px 0 24px rgba(0,0,0,.12)',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform .26s cubic-bezier(.2,.8,.3,1)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* header */}
        <div
          style={{
            padding: '13px 16px',
            borderBottom: '1px solid var(--line)',
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            flexShrink: 0,
          }}
        >
          <Eye size={16} style={{ color: 'var(--ink-2)' }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-1)' }}>
              Proposal preview
            </div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
              {categoryLabel}
            </div>
          </div>
          {/* Refresh button */}
          <button
            onClick={() => fetchPreview(category)}
            disabled={fetching}
            title="Refresh preview (re-fetches saved sections)"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              height: 30,
              padding: '0 10px',
              borderRadius: 7,
              fontSize: 12,
              fontWeight: 500,
              border: '1px solid var(--line)',
              background: 'var(--ivory-0)',
              color: fetching ? 'var(--ink-3)' : 'var(--ink-2)',
              cursor: fetching ? 'not-allowed' : 'pointer',
            }}
          >
            <RotateCcw size={12} style={{ opacity: fetching ? 0.5 : 1 }} />
            {fetching ? 'Loading…' : 'Refresh'}
          </button>
          <button
            onClick={onClose}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: 4,
              color: 'var(--ink-2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={15} />
          </button>
        </div>

        {/* body */}
        <div style={{ flex: 1, minHeight: 0, position: 'relative', display: 'flex', flexDirection: 'column' }}>
          {/* Loading state */}
          {fetching && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--ivory-2)',
                zIndex: 2,
              }}
            >
              <div style={{ textAlign: 'center' }}>
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    border: '3px solid var(--line)',
                    borderTopColor: 'var(--saffron)',
                    margin: '0 auto 12px',
                    animation: 'spin 0.7s linear infinite',
                  }}
                />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
                  Generating preview…
                </div>
              </div>
            </div>
          )}

          {/* Error state */}
          {fetchError && !fetching && (
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                padding: 24,
                background: 'var(--ivory-2)',
              }}
            >
              <p style={{ margin: 0, fontSize: 13, color: '#b91c1c', textAlign: 'center' }}>
                {fetchError}
              </p>
              <button
                onClick={() => fetchPreview(category)}
                style={{
                  height: 32,
                  padding: '0 14px',
                  borderRadius: 8,
                  fontSize: 12,
                  border: '1px solid var(--line)',
                  background: 'var(--ivory-0)',
                  color: 'var(--ink-2)',
                  cursor: 'pointer',
                }}
              >
                Retry
              </button>
            </div>
          )}

          {/* Sandboxed iframe — proposal's own CSS is isolated from the admin app */}
          {html && !fetching && (
            <iframe
              srcDoc={html}
              sandbox="allow-same-origin"
              style={{
                flex: 1,
                width: '100%',
                border: 'none',
                background: '#fff',
              }}
              title={`Preview — ${categoryLabel}`}
            />
          )}

          {/* Empty state before first fetch */}
          {!html && !fetching && !fetchError && (
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--ivory-2)',
                color: 'var(--ink-3)',
                fontSize: 13,
              }}
            >
              Loading…
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Create section modal ─────────────────────────────────────────────────────

function CreateSectionModal({
  categoryKey,
  onClose,
  onCreate,
}: {
  categoryKey: string;
  onClose: () => void;
  onCreate: (slug: string, name: string) => void;
}) {
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');

  const inputStyle: React.CSSProperties = {
    width: '100%',
    height: 36,
    padding: '0 10px',
    fontSize: 13.5,
    border: '1px solid var(--line)',
    borderRadius: 7,
    background: 'var(--ivory-0)',
    color: 'var(--ink-1)',
    outline: 'none',
    boxSizing: 'border-box',
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(27,23,19,.4)',
          backdropFilter: 'blur(2px)',
        }}
      />
      <div
        style={{
          position: 'relative',
          width: 400,
          background: 'var(--ivory-0)',
          borderRadius: 14,
          padding: 24,
          boxShadow: '0 12px 40px rgba(0,0,0,.18)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 18,
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: 16,
              fontWeight: 700,
              color: 'var(--ink-1)',
            }}
          >
            New section
          </h3>
          <button
            onClick={onClose}
            style={{
              width: 28,
              height: 28,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--ink-3)',
              borderRadius: 6,
              padding: 0,
            }}
          >
            <X size={16} />
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.07em',
                color: 'var(--ink-3)',
                display: 'block',
                marginBottom: 5,
              }}
            >
              Section name
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Terms & Conditions"
              style={inputStyle}
              autoFocus
            />
          </div>
          <div>
            <label
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.07em',
                color: 'var(--ink-3)',
                display: 'block',
                marginBottom: 5,
              }}
            >
              Slug (lowercase, underscores only)
            </label>
            <input
              value={slug}
              onChange={(e) =>
                setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))
              }
              placeholder="e.g. terms_and_conditions"
              style={inputStyle}
            />
          </div>
          <div
            style={{
              display: 'flex',
              gap: 8,
              marginTop: 4,
              justifyContent: 'flex-end',
            }}
          >
            <button
              onClick={onClose}
              style={{
                height: 34,
                padding: '0 14px',
                borderRadius: 8,
                fontSize: 13,
                border: '1px solid var(--line)',
                background: 'var(--ivory-0)',
                color: 'var(--ink-2)',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (slug && name) onCreate(slug, name);
              }}
              disabled={!slug || !name}
              style={{
                height: 34,
                padding: '0 14px',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                border: 'none',
                background: slug && name ? 'var(--ink-1)' : 'var(--ivory-2)',
                color: slug && name ? 'var(--ivory-0)' : 'var(--ink-3)',
                cursor: slug && name ? 'pointer' : 'not-allowed',
              }}
            >
              Create
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function ProposalBuilderPage() {
  const [categories, setCategories] = useState<ProposalCategory[]>([]);
  const [category, setCategory] = useState<string>('');
  const [sections, setSections] = useState<ProposalSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<SaveToast>({ visible: false, msg: '' });
  const [showCreate, setShowCreate] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  // REAL default-rendered HTML per section key for the active category, so the
  // editor loads the actual proposal markup (not a stub) when no override exists.
  const [realSectionHtml, setRealSectionHtml] = useState<Record<string, string>>({});
  // The proposal's own stylesheet, so the editor renders sections exactly like
  // the proposal (injected into the editor's isolated iframe).
  const [proposalCss, setProposalCss] = useState<string>('');
  // Editor panel sizing: drag-resizable width + fullscreen overlay.
  const [editorWidth, setEditorWidth] = useState<number>(460);
  const [editorFullscreen, setEditorFullscreen] = useState(false);
  const resizeRef = useRef<{ startX: number; startW: number } | null>(null);
  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    resizeRef.current = { startX: e.clientX, startW: editorWidth };
    const onMove = (ev: MouseEvent) => {
      if (!resizeRef.current) return;
      const dx = resizeRef.current.startX - ev.clientX; // drag left → wider
      const w = Math.max(360, Math.min(window.innerWidth - 280, resizeRef.current.startW + dx));
      setEditorWidth(w);
    };
    const onUp = () => {
      resizeRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
    };
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [editorWidth]);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flash = useCallback((msg = 'Saved') => {
    setToast({ visible: true, msg });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(
      () => setToast({ visible: false, msg: '' }),
      2200,
    );
  }, []);

  // Load categories once
  useEffect(() => {
    getCategories()
      .then((cats) => {
        setCategories(cats);
        if (cats.length > 0) setCategory(cats[0].key);
      })
      .catch((e) => setError(e?.message ?? 'Failed to load categories'))
      .finally(() => setLoading(false));
  }, []);

  // Load each section's REAL rendered HTML whenever the category changes, so the
  // editor can show the actual markup. Best-effort — failure just falls back to
  // the descriptor stub. Cleared first so a stale category's HTML never leaks.
  useEffect(() => {
    if (!category) return;
    let cancelled = false;
    setRealSectionHtml({});
    previewSectionHtml(category)
      .then((r) => {
        if (cancelled) return;
        setRealSectionHtml(r.sections ?? {});
        if (r.css) setProposalCss(r.css);
      })
      .catch(() => { /* fall back to stub */ });
    return () => { cancelled = true; };
  }, [category]);

  // Load sections whenever category changes
  const loadSections = useCallback(
    async (cat: string) => {
      setSectionsLoading(true);
      setSelectedId(null);
      try {
        const resp = await getSections(cat);
        setSections(resp.data ?? []);
      } catch (e: any) {
        setError(e?.response?.data?.error ?? e?.message ?? 'Failed to load sections');
      } finally {
        setSectionsLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (category) loadSections(category);
  }, [category, loadSections]);

  const handleToggle = async (id: string) => {
    const sec = sections.find((s) => s.id === id);
    if (!sec || !sec.editable) return;
    const newActive = !sec.isActive;
    setSections((prev) =>
      prev.map((s) => (s.id === id ? { ...s, isActive: newActive } : s)),
    );
    try {
      setSaving(true);
      await patchSection(id, { isActive: newActive });
      flash(newActive ? 'Section shown' : 'Section hidden');
    } catch (e: any) {
      alert(e?.response?.data?.error ?? 'Toggle failed');
      // rollback
      setSections((prev) =>
        prev.map((s) => (s.id === id ? { ...s, isActive: !newActive } : s)),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleMoveUp = async (id: string) => {
    const catSections = sections.filter((s) => s.scope === 'category');
    const idx = catSections.findIndex((s) => s.id === id);
    if (idx <= 0) return;
    const prev = catSections[idx - 1];
    const cur = catSections[idx];
    if (!prev.editable || !cur.editable || !prev.id || !cur.id) return;

    // Swap sortOrders
    const newOrder = [
      { id: cur.id, sortOrder: prev.sortOrder },
      { id: prev.id, sortOrder: cur.sortOrder },
    ];
    setSections((secs) => {
      const copy = [...secs];
      const ai = copy.findIndex((s) => s.id === cur.id);
      const bi = copy.findIndex((s) => s.id === prev.id);
      if (ai === -1 || bi === -1) return secs;
      const tmp = copy[ai];
      copy[ai] = { ...copy[bi], sortOrder: cur.sortOrder };
      copy[bi] = { ...tmp, sortOrder: prev.sortOrder };
      return copy.sort((a, b) => a.sortOrder - b.sortOrder);
    });
    try {
      await reorderSections(newOrder);
      flash('Reordered');
    } catch (e: any) {
      alert(e?.response?.data?.error ?? 'Reorder failed');
      loadSections(category);
    }
  };

  const handleMoveDown = async (id: string) => {
    const catSections = sections.filter((s) => s.scope === 'category');
    const idx = catSections.findIndex((s) => s.id === id);
    if (idx === -1 || idx >= catSections.length - 1) return;
    const cur = catSections[idx];
    const next = catSections[idx + 1];
    if (!next.editable || !cur.editable || !next.id || !cur.id) return;

    const newOrder = [
      { id: cur.id, sortOrder: next.sortOrder },
      { id: next.id, sortOrder: cur.sortOrder },
    ];
    setSections((secs) => {
      const copy = [...secs];
      const ai = copy.findIndex((s) => s.id === cur.id);
      const bi = copy.findIndex((s) => s.id === next.id);
      if (ai === -1 || bi === -1) return secs;
      const tmp = copy[ai];
      copy[ai] = { ...copy[bi], sortOrder: cur.sortOrder };
      copy[bi] = { ...tmp, sortOrder: next.sortOrder };
      return copy.sort((a, b) => a.sortOrder - b.sortOrder);
    });
    try {
      await reorderSections(newOrder);
      flash('Reordered');
    } catch (e: any) {
      alert(e?.response?.data?.error ?? 'Reorder failed');
      loadSections(category);
    }
  };

  // Live-immediate content save. PATCH by id when present; else upsert by
  // key+category (engine-default sections that never had a DB row yet). The
  // returned row carries a real id, which we merge back so the section becomes
  // revert/delete-capable without a reload.
  const handleSaveContent = async (
    target: ProposalSection,
    contentHtml: string,
  ) => {
    setSaving(true);
    try {
      const updated = await saveSectionContent(
        { id: target.id, key: target.key },
        category,
        contentHtml,
      );
      setSections((prev) =>
        prev.map((s) =>
          (target.id && s.id === target.id) ||
          (!target.id && s.key === target.key && s.scope === target.scope)
            ? { ...s, ...updated }
            : s,
        ),
      );
      // Keep the selection pointing at the now-persisted row.
      if (!target.id && updated?.id) setSelectedId(updated.id);
      flash('Saved');
    } catch (e: any) {
      alert(e?.response?.data?.error ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = async (id: string) => {
    if (!window.confirm('Restore this section to the previous saved version?')) return;
    setSaving(true);
    try {
      const updated = await revertSection(id);
      setSections((prev) =>
        prev.map((s) => (s.id === id ? { ...s, ...updated } : s)),
      );
      flash('Reverted to previous save');
    } catch (e: any) {
      alert(e?.response?.data?.error ?? 'Revert failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setSaving(true);
    try {
      await deleteSection(id);
      setSections((prev) => prev.filter((s) => s.id !== id));
      setSelectedId(null);
      flash('Section deleted');
    } catch (e: any) {
      alert(e?.response?.data?.error ?? 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async (slug: string, name: string) => {
    setShowCreate(false);
    setSaving(true);
    try {
      const newSec = await createSection({
        slug,
        name,
        scope: 'category',
        category,
        contentHtml: '',
        sortOrder: 200,
        type: 'static',
      });
      setSections((prev) =>
        [...prev, newSec].sort((a, b) => a.sortOrder - b.sortOrder),
      );
      setSelectedId(newSec.id);
      flash('Section created');
    } catch (e: any) {
      alert(e?.response?.data?.error ?? 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  // Resolve selected section (may be a sentinel id for diamond/variant)
  const selectedSection: ProposalSection | null = (() => {
    if (!selectedId) return null;
    if (selectedId === '__variant__') {
      return {
        id: '__variant__',
        key: 'variant',
        displayName: 'Per-product variant',
        sortOrder: 999,
        scope: 'category',
        editable: false,
        engineDerived: false,
        contentHtml: null,
        images: [],
        isActive: true,
        type: 'variant',
        productCategoryId: null,
      } as unknown as ProposalSection;
    }
    if (selectedId === '__diamond__') {
      return {
        id: '__diamond__',
        key: 'diamond',
        displayName: 'Calculator branch',
        sortOrder: -1,
        scope: 'conditional_calculator',
        editable: false,
        engineDerived: true,
        contentHtml: null,
        images: [],
        isActive: true,
        type: 'engine',
        productCategoryId: null,
      } as unknown as ProposalSection;
    }
    return sections.find((s) => s.id === selectedId || s.key === selectedId) ?? null;
  })();

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: 'var(--ink-3)',
        }}
      >
        Loading…
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <p style={{ color: '#b91c1c', fontSize: 14 }}>{error}</p>
        <button
          onClick={() => {
            setError(null);
            loadSections(category);
          }}
          style={{
            height: 34,
            padding: '0 14px',
            borderRadius: 8,
            border: '1px solid var(--line)',
            background: 'var(--ivory-0)',
            cursor: 'pointer',
            fontSize: 13,
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: 'var(--ivory-2)',
        position: 'relative',
      }}
    >
      {/* Page header */}
      <div
        style={{
          padding: '16px 24px',
          borderBottom: '1px solid var(--line)',
          background: 'var(--ivory-0)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexShrink: 0,
        }}
      >
        <div style={{ flex: 1 }}>
          <h1
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 700,
              color: 'var(--ink-1)',
            }}
          >
            Proposal Builder
          </h1>
          <p style={{ margin: '3px 0 0', fontSize: 12.5, color: 'var(--ink-3)' }}>
            Configure how proposals are assembled per product category
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => setPreviewOpen(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 34,
              padding: '0 14px',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 500,
              border: '1px solid var(--line)',
              background: 'var(--ivory-0)',
              color: 'var(--ink-2)',
              cursor: 'pointer',
            }}
          >
            <Eye size={14} /> Preview
          </button>
          <button
            onClick={() => setShowCreate(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 34,
              padding: '0 14px',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              border: '1px solid var(--line)',
              background: 'var(--ivory-0)',
              color: 'var(--ink-1)',
              cursor: 'pointer',
            }}
          >
            <Plus size={14} /> New section
          </button>
        </div>
      </div>

      {/* Category switcher */}
      <div
        style={{
          padding: '10px 24px',
          borderBottom: '1px solid var(--line)',
          background: 'var(--ivory-0)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          overflowX: 'auto',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: 'var(--ink-3)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            flexShrink: 0,
          }}
        >
          Category
        </span>
        {categories.map((c) => {
          const on = category === c.key;
          return (
            <button
              key={c.key}
              onClick={() => setCategory(c.key)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 12px',
                borderRadius: 8,
                fontSize: 12.5,
                fontWeight: on ? 600 : 500,
                whiteSpace: 'nowrap',
                flexShrink: 0,
                cursor: 'pointer',
                // Active tab: dark ink-0 pill with ivory text + saffron ring so the
                // category NAME is always high-contrast & clearly visible (was the
                // dark-on-dark bug from the undefined --card token).
                border: `1px solid ${on ? 'var(--ink-0)' : 'var(--line)'}`,
                background: on ? 'var(--ink-0)' : 'var(--ivory-0)',
                color: on ? 'var(--ivory-0)' : 'var(--ink-1)',
                boxShadow: on ? '0 0 0 2px var(--saffron-100)' : 'none',
                transition: 'background .12s, border-color .12s, box-shadow .12s',
              }}
            >
              {c.displayName}
              {c.hasCalculator && (
                <span
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    padding: '1px 5px',
                    borderRadius: 4,
                    background: on ? 'rgba(255,255,255,.16)' : '#EFE9F5',
                    color: on ? '#fff' : '#6B5B8C',
                  }}
                >
                  calc
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Body: flowchart + editor */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        {/* Left: flowchart */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            overflowY: 'auto',
            background: 'var(--ivory-2)',
            backgroundImage:
              'radial-gradient(var(--line) 1px, transparent 1px)',
            backgroundSize: '22px 22px',
            position: 'relative',
          }}
        >
          {sectionsLoading ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 200,
                color: 'var(--ink-3)',
                fontSize: 13,
              }}
            >
              Loading sections…
            </div>
          ) : (
            <ProposalFlowchart
              category={category}
              sections={sections}
              selectedId={selectedId}
              onSelect={setSelectedId}
              onToggle={handleToggle}
              onMoveUp={handleMoveUp}
              onMoveDown={handleMoveDown}
            />
          )}
        </div>

        {/* Right: editor */}
        <div
          style={
            editorFullscreen
              ? { position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', background: 'var(--ivory-0)' }
              : { width: editorWidth, flexShrink: 0, display: 'flex', position: 'relative' }
          }
        >
          {/* drag handle (left edge) — hidden in fullscreen */}
          {!editorFullscreen && (
            <div
              onMouseDown={startResize}
              title="Drag to resize the editor"
              style={{ width: 7, cursor: 'ew-resize', flexShrink: 0, background: 'var(--line)', opacity: 0.45 }}
            />
          )}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              borderLeft: editorFullscreen ? 'none' : '1px solid var(--line)',
              background: 'var(--ivory-0)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* mini toolbar: fullscreen toggle */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                padding: '6px 10px',
                borderBottom: '1px solid var(--line)',
                flexShrink: 0,
              }}
            >
              <button
                onClick={() => setEditorFullscreen((f) => !f)}
                title={editorFullscreen ? 'Exit full screen' : 'Full screen'}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  height: 26,
                  padding: '0 10px',
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--ink-2)',
                  background: 'var(--ivory-2)',
                  border: '1px solid var(--line)',
                  borderRadius: 6,
                  cursor: 'pointer',
                }}
              >
                {editorFullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
                {editorFullscreen ? 'Exit full screen' : 'Full screen'}
              </button>
            </div>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              <SectionEditorPanel
                section={selectedSection}
                realHtml={selectedSection ? realSectionHtml[selectedSection.key] : undefined}
                proposalCss={proposalCss}
                fullscreen={editorFullscreen}
                onSaveContent={handleSaveContent}
                onRevert={handleRevert}
                onDelete={handleDelete}
                saving={saving}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Preview drawer */}
      <PreviewDrawer
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        category={category}
        categoryLabel={categories.find((c) => c.key === category)?.displayName ?? category}
      />

      {/* Toast */}
      <SaveToastBanner toast={toast} />

      {/* Create modal */}
      {showCreate && (
        <CreateSectionModal
          categoryKey={category}
          onClose={() => setShowCreate(false)}
          onCreate={handleCreate}
        />
      )}
    </div>
  );
}
